import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { ChevronDown, Menu, X, Phone, Lock } from "lucide-react";
import { useSite, useCompany } from "@/context/SiteContext";

const NAV = [
  { to: "/", label: "Home", end: true },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services", dropdown: true },
  { to: "/projects", label: "Projects" },
  { to: "/process", label: "Process" },
  { to: "/reviews", label: "Reviews" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

const linkCls = ({ isActive }) =>
  `relative py-2 text-[0.82rem] font-semibold uppercase tracking-[0.08em] transition-colors ${isActive ? "text-[color:var(--ib-orange)] after:absolute after:left-0 after:right-0 after:-bottom-0.5 after:h-0.5 after:bg-[color:var(--ib-orange)]" : "text-[color:var(--ib-charcoal)] hover:text-[color:var(--ib-orange)]"}`;

function ServicesMenu({ services }) {
  return (
    <div className="group relative">
      <NavLink to="/services" className={linkCls} data-testid="nav-services">
        <span className="inline-flex items-center gap-1">Services <ChevronDown size={14} /></span>
      </NavLink>
      <div className="invisible absolute left-1/2 top-full z-50 w-64 -translate-x-1/2 pt-3 opacity-0 transition-opacity duration-200 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
        <div className="border-t-2 border-[color:var(--ib-orange)] bg-white py-2 shadow-xl">
          {services.map((s) => (
            <Link key={s.id} to={`/services/${s.slug}`} className="block px-5 py-2.5 text-sm hover:bg-[color:var(--ib-off)] hover:text-[color:var(--ib-orange)]" data-testid={`nav-service-${s.slug}`}>
              {s.title}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

export function Logo({ light = false, className = "h-14" }) {
  return <img src={light ? "/media/logo-light.png" : "/media/logo.png"} alt="Ironbark Construction" className={`${className} w-auto`} />;
}

export default function Header() {
  const { site } = useSite();
  const company = useCompany();
  const [open, setOpen] = useState(false);
  const services = site?.services || [];
  return (
    <header className="sticky top-0 z-40 bg-[#F5F3F0]/95 backdrop-blur-md border-b border-black/5" data-testid="site-header">
      <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-6 px-5 py-3 lg:px-8">
        <Link to="/" data-testid="header-logo-link" aria-label="Ironbark Construction home"><Logo className="h-11 sm:h-14" /></Link>
        <div className="hidden flex-col items-end gap-2 lg:flex">
          <p className="text-[0.7rem] font-medium uppercase tracking-[0.18em] text-[color:var(--ib-ink)]">
            {company.license_label || "General Contractor"} <span className="mx-2 text-black/30">|</span> Lic. #{company.license}
            <span className="mx-2 text-black/30">|</span><Link to="/admin/login" className="inline-flex items-center gap-1 hover:text-[color:var(--ib-orange)]" data-testid="header-admin-login-link"><Lock size={11} /> Admin login</Link>
          </p>
          <nav className="flex items-center gap-7" aria-label="Main">
            {NAV.map((n) => n.dropdown ? <ServicesMenu key={n.to} services={services} /> : (
              <NavLink key={n.to} to={n.to} end={n.end} className={linkCls} data-testid={`nav-${n.label.toLowerCase()}`}>{n.label}</NavLink>
            ))}
            <Link to="/contact" className="ib-btn ib-btn-primary !py-2.5 !px-5" data-testid="header-get-quote-btn">Get a Quote</Link>
          </nav>
        </div>
        <button className="lg:hidden p-2" onClick={() => setOpen(!open)} aria-label="Toggle menu" data-testid="mobile-menu-toggle">
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open && (
        <nav className="lg:hidden border-t border-black/10 bg-white px-5 pb-6 pt-2" data-testid="mobile-menu">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)} className="block border-b border-black/5 py-3 font-display text-lg uppercase">{n.label}</NavLink>
          ))}
          {services.map((s) => (
            <Link key={s.id} to={`/services/${s.slug}`} onClick={() => setOpen(false)} className="block py-2 pl-4 text-sm text-black/70">{s.title}</Link>
          ))}
          <div className="mt-4 flex gap-3">
            <Link to="/contact" onClick={() => setOpen(false)} className="ib-btn ib-btn-primary flex-1 justify-center">Get a Quote</Link>
            <a href={`tel:${(company.phone || "").replace(/\D/g, "")}`} className="ib-btn ib-btn-outline" aria-label="Call us"><Phone size={16} /></a>
          </div>
          <Link to="/admin/login" onClick={() => setOpen(false)} className="mt-4 flex items-center gap-2 text-xs uppercase tracking-widest text-black/60" data-testid="mobile-admin-login-link"><Lock size={12} /> Owner login</Link>
        </nav>
      )}
    </header>
  );
}
